import { useEffect, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Cpu, KeyRound, Check } from 'lucide-react';
import { api } from '../lib/api';
import { SectionCard, Field, Skeleton } from '../components/pipeline/ui';
import { CreditAmount } from '../components/wallet/CreditUI';
import BuyBundlesCard from '../components/wallet/BuyBundlesCard';
import RedeemCard from '../components/wallet/RedeemCard';
import ReferralCard from '../components/wallet/ReferralCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

// Pure, credit-only settings view. Tokens NEVER surface here (A6 sealing). Billing provider is
// an internal concern and is not shown to users (A7). Balances render via the shared formatter (A8).
export function SettingsView({ wallet, peg = 0.01, meteringEnabled = false, ledger = [], activeModel = null }) {
  const { t } = useTranslation();
  const balance = wallet?.balance_credits ?? 0;
  return (
    <>
      <SectionCard title={t('settings.billingTitle')} testId="settings-billing-card">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={<T k="settings.activeModel" />}>
            {/* N-30: this was the literal string "Anthropic · claude-sonnet-4-6" — wrong the moment
                the platform default moved, and the one place a user looks to see what model they
                are on. Now the RESOLVED model, from the same dry-run resolver the round uses
                (walletPreview → model_id), passed in by the page. No provider name is invented:
                the server does not return one, and guessing it is how the lie started. */}
            <span className="flex items-center gap-2" data-testid="settings-active-model">
              <Cpu className="h-4 w-4" /> {activeModel || '—'}
            </span>
          </Field>
          <Field label={<T k="settings.creditsMode" />}>
            {meteringEnabled ? t('settings.creditsEnabled') : t('settings.creditsOff')}
          </Field>
          <Field label={<T k="settings.walletBalance" />}>
            <CreditAmount credits={balance} peg={peg} testId="settings-wallet-balance" />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title={t('settings.usageTitle')} testId="settings-usage-card">
        {!meteringEnabled && (
          <p className="text-sm text-muted-foreground" data-testid="settings-metering-off"><T k="settings.meteringOffNote" /></p>
        )}
        {ledger.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="settings-ledger-empty"><T k="settings.ledgerEmpty" /></p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="usage-ledger-table">
              <thead>
                <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-start py-2 pe-4"><T k="settings.colAction" /></th>
                  <th className="text-start py-2 pe-4"><T k="settings.colAmount" /></th>
                  <th className="text-start py-2"><T k="settings.colWhen" /></th>
                </tr>
              </thead>
              <tbody className="font-data">
                {ledger.slice(0, 20).map((e) => (
                  <tr key={e.id} className="border-b border-border" data-testid={`ledger-row-${e.id}`}>
                    <td className="py-2 pe-4 text-foreground">
                      {/* N-34: 'round' is the server's collapsed settled-round row (the actual
                          charge, not hold plumbing) — the one action worth a localized label.
                          Anything else renders raw, as before. */}
                      {e.action === 'round' ? t('wallet.actionRound') : e.action === 'ingest' ? t('wallet.actionIngest') : e.action}
                      {e.paid_irr != null && (
                        <span className="ms-2 text-xs text-muted-foreground" data-testid={`ledger-paid-${e.id}`}>
                          {e.bundle_label ? `${e.bundle_label} · ` : ''}{Number(e.paid_irr).toLocaleString()} {t('wallet.rial')}
                        </span>
                      )}
                    </td>
                    <td className={`py-2 pe-4 ${e.direction === 'debit' ? 'text-destructive' : 'text-success'}`}>
                      <span data-testid={`ledger-credits-${e.id}`}>
                        {e.direction === 'debit' ? '−' : '+'}<CreditAmount credits={e.credits} peg={peg} testId={`ledger-amount-${e.id}`} />
                      </span>
                    </td>
                    <td className="py-2 text-muted-foreground">{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </>
  );
}

// B1 + per-user keys: pick a persisted default model from the user-selectable whitelist (all
// providers), and optionally bring YOUR OWN API key for the selected model's provider. "Account
// default" clears the model choice. Keys are keyed by provider — changing the model never wipes one.
export function DefaultModelCard() {
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  const [value, setValue] = useState('__default__');
  const [saving, setSaving] = useState(false);
  const [keyStates, setKeyStates] = useState({}); // { [provider]: key_set }
  const [keyAvail, setKeyAvail] = useState({}); // { [model_id]: has_key } — user's own OR platform key
  const [keyInput, setKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);

  const loadKeys = () =>
    api.listUserProviderKeys()
      .then((rows) => {
        const m = {};
        (rows || []).forEach((r) => { m[r.provider] = !!r.key_set; });
        setKeyStates(m);
      })
      .catch(() => {});

  useEffect(() => {
    api.listSelectableModelsFull().then((r) => setModels(r || [])).catch(() => {});
    api.getMe().then((r) => setValue(r.default_model_id || '__default__')).catch(() => {});
    api.modelKeyAvailability().then((r) => {
      const m = {};
      (r?.models || []).forEach((x) => { m[x.model_id] = !!x.has_key; });
      setKeyAvail(m);
    }).catch(() => {});
    loadKeys();
  }, []);

  const selectedModel = value === '__default__' ? null : models.find((m) => m.model_id === value);
  const selectedProvider = selectedModel?.provider || null;
  const providerKeySet = selectedProvider ? !!keyStates[selectedProvider] : false;

  const save = async (v) => {
    setValue(v);
    setSaving(true);
    try {
      await api.setDefaultModel(v === '__default__' ? null : v);
      toast.success(t('settings.saved'));
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const saveKey = async () => {
    if (!selectedProvider || !keyInput.trim()) return;
    setSavingKey(true);
    try {
      await api.upsertUserProviderKey(selectedProvider, keyInput.trim());
      setKeyInput('');
      toast.success(t('settings.keySaved'));
      await loadKeys();
    } catch (e) { toast.error(e.message); } finally { setSavingKey(false); }
  };

  return (
    <SectionCard title={t('settings.defaultModelTitle')} testId="settings-default-model-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="settings.defaultModelHint" /></p>
      <Select value={value} onValueChange={save} disabled={saving}>
        <SelectTrigger className="max-w-sm" data-testid="default-model-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__default__" data-testid="default-model-account"><T k="settings.defaultModelNone" /></SelectItem>
          {models.map((m) => {
            const noKey = keyAvail[m.model_id] === false;
            return (
              <SelectItem key={m.model_id} value={m.model_id} disabled={noKey} data-testid={`default-model-${m.model_id}`}>
                {(m.display_label || m.model_id) + (m.tier_hint ? ` · ${m.tier_hint}` : '') + (noKey ? ` · ${t('settings.modelNoKey')}` : '')}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {selectedProvider && (
        <div className="mt-5 pt-5 border-t border-border flex flex-col gap-2" data-testid="provider-key-block">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {t('settings.providerKeyLabel', { provider: selectedProvider })}
            </span>
            {providerKeySet ? (
              <span className="inline-flex items-center gap-1 text-xs text-success" data-testid="provider-key-status-set">
                <Check className="h-3.5 w-3.5" /> {t('settings.providerKeySet')}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground" data-testid="provider-key-status-unset"><T k="settings.providerKeyNotSet" /></span>
            )}
          </div>
          <p className="text-xs text-muted-foreground"><T k="settings.providerKeyHint" /></p>
          <div className="flex items-center gap-2 max-w-sm">
            <Input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder={t('settings.providerKeyPlaceholder', { provider: selectedProvider })}
              className="text-sm"
              data-testid="provider-key-input"
            />
            <Button onClick={saveKey} disabled={savingKey || !keyInput.trim()} data-testid="provider-key-save">
              {savingKey ? t('settings.saving') : t('settings.saveKey')}
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

const PROFILE_FIELDS = ['role_context', 'expertise', 'industry_background', 'known_pain_points'];

// B2: free-text profile that grounds AI idea suggestions (injected server-side as USER CONTEXT).
function ProfileCard() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getMe().then((r) => setProfile(r.profile || {})).catch(() => setProfile({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.setProfile(profile);
      toast.success(t('settings.saved'));
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (!profile) return <Skeleton className="h-40 w-full" />;
  return (
    <SectionCard title={t('settings.profileTitle')} testId="settings-profile-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="settings.profileHint" /></p>
      <div className="grid sm:grid-cols-2 gap-4">
        {PROFILE_FIELDS.map((f) => (
          <div key={f} className="flex flex-col gap-1.5">
            <label className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground" htmlFor={`profile-${f}`}>
              {t(`settings.profile_${f}`)}
            </label>
            <Textarea
              id={`profile-${f}`}
              value={profile[f] || ''}
              onChange={(e) => setProfile({ ...profile, [f]: e.target.value })}
              placeholder={t(`settings.profile_${f}_ph`)}
              className="min-h-[72px] resize-y text-sm"
              data-testid={`profile-input-${f}`}
            />
          </div>
        ))}
      </div>
      {/* end-aligned, as the card is drawn. A bare div left it against the start edge. */}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="profile-save">
          {saving ? t('settings.saving') : t('settings.save')}
        </Button>
      </div>
    </SectionCard>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  // N-30: the model this account would ACTUALLY run on, resolved by the same server path a real
  // round uses (per-call pick → user default → platform default). Label from the selectable list
  // when the model appears there; the raw model_id otherwise (a non-selectable default is still
  // the truth, and the truth beats a prettier lie).
  const [activeModel, setActiveModel] = useState(null);

  useEffect(() => {
    api.out('get_usage').then(setData).catch(() => setData({}));
    Promise.all([
      api.walletPreview({ corpus_used: false }),
      api.listSelectableModels().catch(() => ({ models: [] })),
    ]).then(([prev, sel]) => {
      const id = prev?.model_id || null;
      if (!id) return; // leave the placeholder — never invent a model name
      const match = (sel?.models || []).find((m) => m.model_id === id);
      setActiveModel(match?.display_label ? `${match.display_label} (${id})` : id);
    }).catch(() => {});
  }, []);

  // Gateway return leg: the payment callback lands here with ?payment=…&order_id=…. The query is
  // a hint, not the truth — anything but a clean success is re-verified server-side before the
  // toast, then the query is stripped so a reload doesn't re-announce.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const payment = q.get('payment');
    if (!payment) return;
    const orderId = q.get('order_id');
    const finish = (ok) => {
      if (ok) toast.success(t('wallet.paySuccess'));
      else toast.error(t('wallet.payFailed'));
      navigate('/settings/billing', { replace: true });
      api.out('get_usage').then(setData).catch(() => {});
    };
    if (payment === 'success') finish(true);
    else if (orderId) {
      api.walletVerifyCheckout(orderId)
        .then((r) => finish(r.status === 'paid'))
        .catch(() => finish(false));
    } else finish(false);
  }, [navigate, t]);

  // Which of the three sections is open comes from the ROUTE, not from component state, so each is
  // linkable and the sidebar can show which one you are on.
  const section = pathname.endsWith('/billing') ? 'billing' : pathname.endsWith('/llm') ? 'llm' : 'profile';
  const SECTIONS = [
    ['profile', 'settings.tabProfile', '/settings'],
    ['billing', 'settings.tabBilling', '/settings/billing'],
    ['llm', 'settings.tabModel', '/settings/llm'],
  ];

  return (
    <>
      <h1 className="font-heading text-3xl font-bold"><T k="settings.title" /></h1>
      <div className="mt-5 border-t border-border" />

      {/* The side menu repeats the sidebar's Settings children, as the mock does. Content swaps;
          chrome stays. */}
      <div className="mt-5 grid gap-7 md:grid-cols-[210px_1fr]">
        <nav data-testid="settings-subnav">
          {SECTIONS.map(([key, label, to]) => (
            <button
              key={key}
              type="button"
              onClick={() => navigate(to)}
              data-testid={`settings-tab-${key}`}
              aria-current={section === key ? 'page' : undefined}
              className={`mb-0.5 flex w-full items-center rounded-lg px-2.5 py-1.5 text-start text-[13.5px]
                ${section === key ? 'bg-accent font-bold text-accent-foreground' : 'text-foreground hover:bg-accent'}`}
            >
              {t(label)}
            </button>
          ))}
        </nav>

        {/* Every card that existed before still exists — this is a restructure, not a trim. */}
        <div className="flex min-w-0 flex-col gap-6">
          {section === 'profile' && <ProfileCard />}
          {section === 'billing' && (
            !data ? <Skeleton className="h-40 w-full" /> : (
              <>
                <SettingsView
                  activeModel={activeModel}
                  wallet={data.wallet}
                  peg={data.credit_usd_peg ?? 0.01}
                  meteringEnabled={!!data.metering_enabled}
                  ledger={data.ledger || []}
                />
                <BuyBundlesCard />
                <RedeemCard peg={data?.credit_usd_peg ?? 0.01} />
                <ReferralCard />
              </>
            )
          )}
          {section === 'llm' && <DefaultModelCard />}
        </div>
      </div>
    </>
  );
}